import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { Button, Col, Row } from 'reactstrap';
import axios from 'axios';
import { get, some } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactLoading from 'react-loading';
import { Cookies } from 'react-cookie';

import RealizedPricesTable from './RealizedPricesTable';
import EditLotModal from '../Step3/EditLotModal';
import IsPremiumBuyerModal from './IsPremiumBuyerModal';

import {
  validateRealizedPrice,
  validateLotCatalog
} from '../diy-utils/EditLotValidation';

import getSpreadsheetError from '../diy-utils/getSpreadsheetError';
import { handleSelectAllById, handleRowCheckbox } from '../diy-utils/checkboxHandlers';
import ConfirmModal from '../../../utilities/form-components/ConfirmModal';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import { errorLoadingRealized } from '../../../utilities/StaticText';

// After a user successfully uploads a spreadsheet, we arrive here
class RealizedPricesSuccessUpload extends Component {
  constructor(props) {
    super(props);

    // This only ever happens once so we're configuring an initial state for lots - which is ok.
    this.state = {
      currentPage: 0,
      deleteLotModal: false,
      deleteSelectedLotsModal: false,
      editLotModal: false,
      error: null,
      invNumberArray: [],
      itemsPerPage: 50,
      loading: false,
      lotData: null,
      lotNumberArray: [],
      lots: props.lots,
      rowIndex: null,
      selectedLots: new Set(),
      showOnlyErrors: false,
      spreadsheetErrors: props.spreadsheetErrors,
      spreadsheetWarnings: props.spreadsheetWarnings,
      uploadAgainModal: false,
      editType: props.editType,
      isOpenPremiumBuyerModal: false
    };
  }

  deleteLot = () => {
    const { id } = this.state;

    const lotsCopy = [...this.state.lots];
    const lotIndex = lotsCopy.findIndex(lot => lot.id === id);
    const deletedLotNumber = lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt;
    lotsCopy.splice(lotIndex, 1);

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(lotsCopy, [
      deletedLotNumber
    ]);

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      lots: resolvedLots,
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });
  };

  /**
   * updateSpreadsheetErrors takes in the array of lots in the spreadsheet (after deletion),
   * and an array of all the deleted lot numbers.
   *
   * The errors are handled in two parts:
   *
   * 1. The spreadsheetErrors JSON object in state which contains grouped errors
   * by error code, and each error group contains all affected lots in an ES6 set.
   *
   * 2. The hasError attribute for the lot object in the table - this allows us to
   * call a filter for hasError = true in the table.  If the lot is being deleted - this
   * doesn't matter.  If the lot is being edited - it can no longer have errors so we
   * set hasError to false in the submitEditLot function.
   *
   * This function will parse the existing spreadsheet errors and remove all
   * references to every lot in the spreadsheetErrors object.  If there are no more lotNumbers
   * for an existing error - delete the entire error out of the spreadsheetErrors
   * object.
   *
   * Note that there are two special cases in this function for duplicate lot # and duplicate
   * inventory #.  These cases also have to find associated duplicates and
   * sometimes set hasError to false.
   *
   * This function is called whenever a user deletes a lot, deletes selected lots,
   * or edits a lot so that we can update the list of errors the user sees.
   *
   * The returned values are:
   * 1. The updated lot array (resolvedLots)
   * 2. The updated spreadsheetErrors JSON object (resolvedErrors)
   */
  updateSpreadsheetErrors = (lots, lotNumberArray) => {
    const lotsCopy = [...lots];
    const spreadsheetErrorCopy = this.state.spreadsheetErrors;
    if (!spreadsheetErrorCopy) {
      return { resolvedLots: lots, resolvedErrors: null };
    }

    lotNumberArray.forEach(lot => {
      Object.keys(spreadsheetErrorCopy).forEach(errorCode => {
        const errorsForCode = spreadsheetErrorCopy[errorCode];

        // Special snowflake workarounds for duplicate lot number / inventory number cases
        if (errorCode === '110') {
          // If duplicate lot number... filter the lot array to find all lots with this number
          const numDuplicates = lotsCopy.filter(
            filteredLot => filteredLot.lotNumber + filteredLot.lotExt === lot
          ).length;

          /**
           * If one or zero lots remain (the user fixed the duplicate) - remove the hasError field
           * and remove it from the set.
           */
          if (numDuplicates <= 1) {
            errorsForCode.lotNumbers.delete(lot);
            const index = lotsCopy.findIndex(
              matchingLot => matchingLot.lotNumber + matchingLot.lotExt === lot
            );

            if (index >= 0) {
              lotsCopy[index].hasError = false;
            }
          }
        } else if (errorCode === '121') {
          // Note that in this case we need the value from state before it was deleted
          const stateLotCopy = [...this.state.lots];

          // Get the inventory number of the culprit
          const invNumberToCheck = stateLotCopy.find(
            filteredLot => filteredLot.lotNumber + filteredLot.lotExt === lot
          ).invNumber;

          // Find out how many duplicates there are remaining...
          const duplicateLots = lotsCopy.filter(
            filteredLot => filteredLot.invNumber === invNumberToCheck
          );

          const numDuplicates = duplicateLots.length;

          // if num duplicates is 1 - remove the error from the banner and from the table
          if (numDuplicates === 1) {
            const index = lotsCopy.findIndex(
              matchingLot => matchingLot.invNumber === duplicateLots[0].invNumber
            );

            if (index >= 0) {
              errorsForCode.lotNumbers.delete(lotsCopy[index].lotNumber + lotsCopy[index].lotExt);
              lotsCopy[index].hasError = false;
            }
          }

          // Always remove the modified/deleted lot since it must now be correct
          errorsForCode.lotNumbers.delete(lot);
        } else {
          /**
           * Set delete will just return false if it didn't delete
           * so we don't have to check if it exists first.  This is
           * faster than checking if it exists. (Tested on jsben.ch)
           */
          errorsForCode.lotNumbers.delete(lot);
        }

        if (errorsForCode.lotNumbers.size === 0) {
          delete spreadsheetErrorCopy[errorCode];
        }
      });
    });

    return { resolvedLots: lotsCopy, resolvedErrors: spreadsheetErrorCopy };
  };

  deleteSelectedLots = () => {
    const lotsCopy = [...this.state.lots];
    const { selectedLots } = this.state;

    const deletedLotNumbers = [];
    const reverseArray = [...selectedLots].sort((a, b) => b - a);
    reverseArray.forEach(lotId => {
      const lotIndex = lotsCopy.findIndex(lot => lot.id === lotId);
      if (lotIndex >= 0) {
        deletedLotNumbers.push(lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt);
        lotsCopy.splice(lotIndex, 1);
      }
    });

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(
      lotsCopy,
      deletedLotNumbers
    );

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      currentPage: 0,
      lots: resolvedLots,
      selectedLots: new Set(),
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });
  };

  editLot = id => {
    const { lots } = this.state;
    const lotData = lots.find(lot => lot.id === id);

    // Show edit lot modal - pass in the selected lot data
    this.handleModal('editLotModal', true, lotData, id);
  };

  // Toggle a table row as selected or unselected by the id
  handleCheckbox = rowIndex => {
    const { selectedLots } = this.state;
    this.setState({ selectedLots: handleRowCheckbox(rowIndex, selectedLots) });
  };

  // Select all or unselect all items currently on the page
  handleCheckboxAll = () => {
    const { currentPage, itemsPerPage, showOnlyErrors, selectedLots } = this.state;
    let lotsCopy = [...this.state.lots];
    if (showOnlyErrors) {
      lotsCopy = lotsCopy.filter(lot => lot.hasError);
    }

    this.setState({
      selectedLots: handleSelectAllById(currentPage, itemsPerPage, lotsCopy, selectedLots)
    });
  };

  handleErrorCheckboxFilter = () => {
    const { showOnlyErrors } = this.state;
    this.setState({
      currentPage: 0,
      selectedLots: new Set(),
      showOnlyErrors: !showOnlyErrors
    });
  };

  // Reusable modal handler
  handleModal = (modalName, showModal, lotData = null, id = null) => {
    /**
     * We need to do some initial validation for the user before the edit lot modal launches
     * so that we can load the modal in an errored state
     */
    if (modalName === 'editLotModal' && showModal) {
      const { lots } = this.state;
      const {
        lotNumber,
        lotExt,
        realizedPrice
      } = lotData;

      const lotNumberArray = lots.map(lot => lot.lotNumber + lot.lotExt);

      /**
       * Remove the first occurrence of the maps that match so that
       * we can do duplicate checks in validation
       */
      const lotNumberIndex = lotNumberArray.findIndex(lotNum => lotNum === lotNumber + lotExt);
      lotNumberArray.splice(lotNumberIndex, 1);

      const { lotNumber: validatedLotNumberCatalog,
        lotExt: validatedLotExtCatalog } = validateLotCatalog(
        lotNumber,
        lotExt,
        lotNumberArray,
        this.state.lots
      );

      const editLotValidation = {
        lotNumber: validatedLotNumberCatalog,
        lotExt: validatedLotExtCatalog,
        realizedPrice: validateRealizedPrice(realizedPrice)
      };

      //check if the lot is on spreadsheetErrors on Lot Not Found code.
      let lotNotFound = null;
      if (this.state.spreadsheetErrors[149] !== undefined
          && this.state.spreadsheetErrors[149].lotNumbers.has(lotData.lotNumber)) {
        lotNotFound = lotData.lotNumber;
      }

      this.setState({
        [modalName]: showModal,
        lotData,
        editLotValidation,
        id,
        lotNumberArray,
        lotNotFound
      });
    } else {
      this.setState({
        [modalName]: showModal,
        lotData,
        id
      });
    }
  };

  getUserId = () => {
    const cookies = new Cookies();
    const userID = cookies.get('userID');
    return parseInt(userID, 10);
  }

  validateFieldsRealized = async (lotData, validation) => {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    let returnValue = null;

    lotData.changeUserID = this.getUserId();
    try {
      const postUrl = `/cps/app/catalog/${catalogID}/lot/realized`;
      await axios.post(postUrl, lotData,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });
      validation.lotNumber = null;
      validation.lotExt = null;
      returnValue = validation;
    } catch (err) {
      if (err.response.data.errors[0].messageId === 'CPS_LOT_NOT_FOUND') {
        validation.lotNumber = 'This lot number was not a part of this catalog.';
      }
      returnValue = validation;
    }
    return returnValue;
  };

  // The user changed the pagination value for the table
  handlePageClick = data => {
    this.setState({ currentPage: data.selected });
  };

  // The user changed the number of lots per page - set the current page back to 0
  handlePageSize = e => {
    // For some reason, even though the value is a number, this is being changed to a string
    this.setState({ currentPage: 0, itemsPerPage: +e.target.value });
  };

  // The user edited their lot data inside the modal and there are no more errors!
  submitEditLot = lotData => {
    const lotsCopy = [...this.state.lots];
    const lotIndex = lotsCopy.findIndex(lot => lot.id === lotData.id);
    const updatedLot = lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt;
    lotsCopy[lotIndex] = lotData;
    lotsCopy[lotIndex].hasError = false;

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(lotsCopy, [updatedLot]);

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      editLotModal: false,
      lots: resolvedLots,
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });

    // Save
    this.submitLotData([lotData], false);
    this.setState({ loading: false });
  };

  async submitLotData(lotData, isPublish, isPremiumValue) {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    this.setState({ loading: true });

    // Delete the unique lot id field and hasError field that the FE had to add
    const lotsCopy = lotData;
    lotsCopy.forEach(lot => {
      delete lot.hasError;
    });

    let lotToSend = null;
    let lotUrl = 'lots';
    if (isPublish) {
      lotToSend = lotsCopy.map(lot => ({
        lotNumber: lot.lotNumber,
        lotExt: lot.lotExt,
        realizedPrice: lot.realizedPrice,
        changeUserID: this.getUserId()
      }));
      lotToSend = { lots: lotToSend };
    } else {
      lotUrl = 'lot';
      lotToSend = lotsCopy[0];
      lotToSend.changeUserID = this.getUserId();
    }

    try {
      const postUrl = `/cps/app/catalog/${catalogID}/${lotUrl}/realized`;
      if (lotUrl === 'lots') {
        // includePremium api
        const premiumUrl = `/cps/app/catalog/${catalogID}/realized/includePremium`;
        await Promise.all([
          axios.post(premiumUrl, isPremiumValue,
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              }
            }),
          axios.post(postUrl, lotToSend,
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              }
            })
        ]);
      } else {
        axios.post(postUrl, lotToSend,
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          });
      }
    } catch (error) {
      this.setState({ error, loading: false });
    }
  }

  // has to send to new api the full list of lots to update
  // called by Publish.
  async saveAndExit(props) {
    await this.submitLotData([...this.state.lots], true, props);
    this.props.history.push('/my-auctions/current');
  }

  setModal(value) {
    this.setState(
      { ...this.state, isOpenPremiumBuyerModal: value }
    );
  }

  handleOpen = () => {
    this.setModal(true);
  };

  handleClose = () => {
    this.setModal(false);
  };

  onSave = (props) => {
    this.setModal(false);
    this.saveAndExit(props);
  };

  toggleDeleteModal = () => {
    this.setState({ deleteLotModal: !this.state.deleteLotModal });
  };

  toggleDeleteSelectedLotsModal = () => {
    this.setState({ deleteSelectedLotsModal: !this.state.deleteSelectedLotsModal });
  };

  toggleUploadAgainModal = () => {
    this.setState({ uploadAgainModal: !this.state.uploadAgainModal });
  };

  render() {
    const { error, loading } = this.state;

    if (error) {
      return <InlineAlert baseText={errorLoadingRealized} />;
    }

    if (loading) {
      return (
        <div className="loading-spinner">
          <ReactLoading className="mx-auto mb-4" type="spin" color="black" />
          <p className="font-weight-bold">
            This may take a moment, please do not refresh the browser...
          </p>
        </div>
      );
    }

    const {
      currentPage,
      deleteLotModal,
      deleteSelectedLotsModal,
      lotData,
      editLotValidation,
      editLotModal,
      invNumberArray,
      itemsPerPage,
      lotNumberArray,
      lots,
      selectedLots,
      showOnlyErrors,
      spreadsheetErrors,
      spreadsheetWarnings,
      uploadAgainModal
    } = this.state;

    const originalNumLots = this.props.lots.length;
    const numLots = lots.length;

    const maxLotsExceeded = spreadsheetWarnings.some(warning => warning.errorCode === 113);
    const deletedLots = get(spreadsheetErrors[112], 'lotNumbers', new Set());

    const hasSpreadsheetErrors = some(spreadsheetErrors);

    return (
      <div className="mt-4">
        <Row>
          {/* Start Left Column */}
          <Col xs={12}>
            <strong>Check the Item List below for all required (*) item details.</strong>
            <p>To complete your upload, click &quot;Publish&quot;.</p>

            <strong>Edit One Item (at a time)</strong>
            <p className="mb-0">
              Select the edit icon ( <FontAwesomeIcon icon="edit" /> ) to edit items in the list one
              at a time.
            </p>
            <p>Click &quot;Save Changes&quot; after each edit.</p>

            <strong>Edit Multiple Items</strong>
            <p>
              Edit your original item list file in your spreadsheet program. Resave in the required
              format (.csv, .xls, .xlsx). Click &quot;Go Back &amp; Upload Items Again&quot; to
              replace the collection list below.
            </p>
          </Col>
        </Row>

        {!hasSpreadsheetErrors && (
          <InlineAlert
            baseText="Your file upload was a success!"
            color="success"
            icon="check"
            showPhones={false}
          />
        )}

        {maxLotsExceeded && (
          <InlineAlert
            titleText={`You have reached your maximum of ${originalNumLots} lots for this sale.`}
            baseText={`Your first ${originalNumLots} lots have been uploaded.  Please create a new session to add additional items.`}
            color="warning"
            showPhones={false}
          />
        )}

        {hasSpreadsheetErrors
          && Object.keys(spreadsheetErrors).map(keyIndex => {
            const ssError = spreadsheetErrors[keyIndex];

            // Do some logic here to make the error more friendly
            const { title, description } = getSpreadsheetError(
              keyIndex,
              ssError.displayMsg,
              ssError.lotNumbers
            );

            // BE sends either an array of lot numbers, or null
            const secondaryText = [...ssError.lotNumbers]
              .map(value => (value ? `Lot ${value}` : ''))
              .join(', ');

            return (
              <InlineAlert
                key={keyIndex}
                baseText={description}
                secondaryText={secondaryText}
                showPhones={false}
                titleText={title}
              />
            );
          })}

        <p className="mb-0">
          <strong>Item Summary</strong> (Total # of Items: {numLots})
        </p>
        <p>* = Required</p>

        <RealizedPricesTable
          currentPage={currentPage}
          editLot={this.editLot}
          handleCheckbox={this.handleCheckbox}
          handleCheckboxAll={this.handleCheckboxAll}
          handleErrorCheckboxFilter={this.handleErrorCheckboxFilter}
          handleModal={this.handleModal}
          handlePageClick={this.handlePageClick}
          handlePageSize={this.handlePageSize}
          itemsPerPage={itemsPerPage}
          lots={lots}
          selectedLots={selectedLots}
          showOnlyErrors={showOnlyErrors}
          spreadsheetErrors={spreadsheetErrors}
        />

        <ConfirmModal
          actionFunction={this.props.goBackToUpload}
          bodyText="Are you sure you want to upload these items again? This will delete all the items you just uploaded."
          buttonText="Upload Again"
          showModal={uploadAgainModal}
          title="Upload Items Again"
          toggleFunction={this.toggleUploadAgainModal}
        />

        <ConfirmModal
          actionFunction={this.deleteLot}
          bodyText="Are you sure you want to delete this lot?"
          buttonText="Delete"
          showModal={deleteLotModal}
          title="Delete Lot"
          toggleFunction={this.toggleDeleteModal}
        />

        <ConfirmModal
          actionFunction={this.deleteSelectedLots}
          bodyText="Are you sure you want to set the realized price on all selected lots?"
          buttonText="Set realize"
          showModal={deleteSelectedLotsModal}
          title="Set realize price"
          toggleFunction={this.toggleDeleteSelectedLotsModal}
        />

        <EditLotModal
          deletedLots={deletedLots}
          editLotValidation={editLotValidation}
          handleModal={this.handleModal}
          invNumberArray={invNumberArray}
          lotData={lotData}
          lotNumberArray={lotNumberArray}
          modalName="editLotModal"
          showEditLotModal={editLotModal}
          submitEditLot={this.submitEditLot}
          type={this.state.editType}
          lots={lots}
          validateFieldsRealized={this.validateFieldsRealized}
          lotNotFound={this.state.lotNotFound}
        />

        <IsPremiumBuyerModal
          modal={this.state.isOpenPremiumBuyerModal}
          handleClose={this.handleClose}
          onSave={this.onSave}
        />

        <div className="mt-4">
          <Button
            className={hasSpreadsheetErrors ? 'cursor-not-allowed' : ''}
            disabled={hasSpreadsheetErrors}
            color="primary"
            onClick={() => this.handleOpen()}
          >
            Publish
          </Button>

          <Button
            className="ml-5"
            color="link"
            onClick={() => this.handleModal('uploadAgainModal', true)}
          >
            Go Back &amp; Upload Items Again
          </Button>
        </div>
      </div>
    );
  }
}

export default withRouter(RealizedPricesSuccessUpload);
